import React, { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { ShoppingBag, PoundSterling, Package, TrendingUp, Music2, Store, Info, Circle } from "lucide-react";

// ---- DEMO analytics (replaced by live TikTok Shop / eBay API data once connected) ----
const series = (base) => Array.from({ length: 14 }).map((_, i) => ({
  date: `D${i + 1}`,
  revenue: Math.round(base * (0.6 + Math.sin(i / 2) * 0.25 + Math.random() * 0.3)),
}));

const CHANNELS = {
  tiktok: {
    label: "TikTok Shop", icon: Music2, accent: "#1B4332",
    totals: { orders: 486, revenue: 11240, units: 712, aov: 23.13 },
    revenue_series: series(820),
    top: [
      { name: "Mega Magnesium", units: 184, revenue: 4598 },
      { name: "Ashwagandha", units: 142, revenue: 3123 },
      { name: "Indian Turmeric", units: 96, revenue: 2207 },
      { name: "Creatine Monohydrate", units: 58, revenue: 1391 },
      { name: "Vitamin D3+K2", units: 44, revenue: 879 },
    ],
    orders: [
      { id: "TT-20481", buyer: "@fitlife_jess", item: "Mega Magnesium x2", total: 49.98, status: "To ship", date: "2026-10-07" },
      { id: "TT-20480", buyer: "@gymbrocraig", item: "Creatine Monohydrate", total: 23.99, status: "Shipped", date: "2026-10-07" },
      { id: "TT-20479", buyer: "@wellness.kate", item: "Ashwagandha", total: 21.99, status: "To ship", date: "2026-10-06" },
      { id: "TT-20478", buyer: "@mark_h", item: "Indian Turmeric x2", total: 45.98, status: "Delivered", date: "2026-10-06" },
      { id: "TT-20477", buyer: "@sunnydays22", item: "Vitamin D3+K2", total: 19.99, status: "Shipped", date: "2026-10-05" },
    ],
  },
  ebay: {
    label: "eBay", icon: Store, accent: "#2D6A4F",
    totals: { orders: 312, revenue: 7980, units: 455, aov: 25.58 },
    revenue_series: series(560),
    top: [
      { name: "Indian Turmeric", units: 118, revenue: 2713 },
      { name: "Mega Magnesium", units: 94, revenue: 2349 },
      { name: "Milk Thistle", units: 71, revenue: 1419 },
      { name: "Plant Sterols", units: 52, revenue: 1091 },
      { name: "Echinacea", units: 40, revenue: 760 },
    ],
    orders: [
      { id: "EB-77213", buyer: "greenleaf_uk", item: "Indian Turmeric", total: 22.99, status: "Awaiting dispatch", date: "2026-10-07" },
      { id: "EB-77212", buyer: "healthnut_42", item: "Mega Magnesium x2", total: 49.98, status: "Dispatched", date: "2026-10-07" },
      { id: "EB-77211", buyer: "m.oconnor", item: "Milk Thistle", total: 19.99, status: "Awaiting dispatch", date: "2026-10-06" },
      { id: "EB-77210", buyer: "sarah.b", item: "Plant Sterols", total: 20.99, status: "Delivered", date: "2026-10-05" },
      { id: "EB-77209", buyer: "d_walker", item: "Echinacea x2", total: 37.98, status: "Dispatched", date: "2026-10-05" },
    ],
  },
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-[#e6f0ea] bg-white p-5">
    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-[#f0f7f2]"><Icon size={22} className="text-[#2D6A4F]" /></div>
    <p className="font-display text-3xl font-bold text-[#1B2a21]">{value}</p>
    <p className="mt-1 text-sm text-[#6b7d72]">{label}</p>
  </div>
);

const statusColor = (s) => {
  const t = s.toLowerCase();
  if (t.includes("ship") || t.includes("dispatch")) return t.includes("to ") || t.includes("awaiting") ? "bg-[#fff4e0] text-[#b4790a]" : "bg-[#e3f3ea] text-[#2D6A4F]";
  if (t.includes("deliver")) return "bg-[#eaf4ee] text-[#2D6A4F]";
  return "bg-[#f0f0f0] text-[#777]";
};

const AdminChannels = () => {
  const [tab, setTab] = useState("tiktok");
  const ch = CHANNELS[tab];
  const maxTop = Math.max(...ch.top.map((t) => t.revenue), 1);

  return (
    <div className="p-6 lg:p-8">
      <h1 className="font-display text-3xl font-bold uppercase text-[#1B2a21]">Sales Channels</h1>
      <p className="mb-4 text-[#6b7d72]">Orders & analytics from TikTok Shop and eBay in one place</p>

      <div className="mb-5 flex items-start gap-3 rounded-2xl border border-[#f0d9a0] bg-[#fdf6e6] p-4">
        <Info size={20} className="mt-0.5 shrink-0 text-[#b4790a]" />
        <p className="text-sm text-[#7a5a14]"><strong>Demo preview.</strong> These figures are sample data to show the layout. Once you connect your {ch.label} developer keys and authorise the shop, real orders and analytics will flow in here automatically, and marking an order shipped will push tracking back to {ch.label} and notify the customer.</p>
      </div>

      {/* Channel tabs */}
      <div className="mb-6 flex gap-2">
        {Object.entries(CHANNELS).map(([key, c]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 rounded-full px-6 py-3 font-display text-sm font-bold uppercase tracking-wider transition ${tab === key ? "bg-[#1B4332] text-white" : "bg-white text-[#2D6A4F] border border-[#dceadf] hover:bg-[#f0f7f2]"}`}>
            <c.icon size={18} /> {c.label}
            <span className={`ml-1 flex items-center gap-1 text-[10px] font-semibold ${tab === key ? "text-[#95D5B2]" : "text-[#8aa093]"}`}><Circle size={7} className="fill-current" /> Demo</span>
          </button>
        ))}
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={ShoppingBag} label={`${ch.label} orders`} value={ch.totals.orders} />
        <Stat icon={PoundSterling} label="Revenue" value={`£${ch.totals.revenue.toLocaleString()}`} />
        <Stat icon={Package} label="Units sold" value={ch.totals.units} />
        <Stat icon={TrendingUp} label="Avg order value" value={`£${ch.totals.aov.toFixed(2)}`} />
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="rounded-2xl border border-[#e6f0ea] bg-white p-5 lg:col-span-3">
          <div className="mb-2 flex items-center gap-2"><TrendingUp size={18} className="text-[#2D6A4F]" /><h2 className="font-display text-lg font-bold uppercase text-[#1B2a21]">Revenue (last 14 days)</h2></div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={ch.revenue_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef5f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6b7d72" }} />
              <YAxis tick={{ fontSize: 11, fill: "#6b7d72" }} />
              <Tooltip formatter={(v) => [`£${v}`, "Revenue"]} />
              <Bar dataKey="revenue" fill={ch.accent} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-[#e6f0ea] bg-white p-5 lg:col-span-2">
          <h2 className="mb-4 font-display text-lg font-bold uppercase text-[#1B2a21]">Top Products</h2>
          <div className="space-y-3">
            {ch.top.map((t) => (
              <div key={t.name}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="font-semibold text-[#1B2a21]">{t.name}</span>
                  <span className="text-[#6b7d72]">{t.units} units · £{t.revenue.toLocaleString()}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-[#f0f7f2]"><div className="h-full rounded-full bg-[#2D6A4F]" style={{ width: `${(t.revenue / maxTop) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-[#e6f0ea] bg-white">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="bg-[#f0f7f2] font-display text-xs uppercase tracking-wider text-[#2D6A4F]"><tr><th className="p-4">Order</th><th className="p-4">Buyer</th><th className="p-4">Item</th><th className="p-4">Total</th><th className="p-4">Status</th><th className="p-4">Date</th></tr></thead>
          <tbody>
            {ch.orders.map((o) => (
              <tr key={o.id} className="border-t border-[#f0f5f2]">
                <td className="p-4 font-semibold text-[#1B4332]">{o.id}</td>
                <td className="p-4 text-[#34463c]">{o.buyer}</td>
                <td className="p-4 text-[#6b7d72]">{o.item}</td>
                <td className="p-4 font-semibold text-[#1B4332]">£{o.total.toFixed(2)}</td>
                <td className="p-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusColor(o.status)}`}>{o.status}</span></td>
                <td className="p-4 text-[#6b7d72]">{o.date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminChannels;
